/**
 * Permission helper functions for Zudoku display callbacks
 * All functions are hardcoded with specific permission values to avoid SSR serialization issues
 * Each function is completely self-contained with no external dependencies
 */

/**
 * Verifica solo el permiso del portal (keyway:access:dev_portal)
 */
export const hasPortalPermissionDisplay = () => {
  return ({ auth }: { auth: any }) => {
    if (!auth?.isAuthenticated || !auth?.providerData?.accessToken) {
      return false;
    }

    const portalPerm = 'keyway:access:dev_portal';

    const decodeJwtPayload = (token: string): any => {
      try {
        const parts = token.split(".");
        if (parts.length !== 3) return null;
        const payload = parts[1];
        const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
        const pad = "=".repeat((4 - (base64.length % 4 || 4)) % 4);
        const binString = typeof atob !== "undefined"
          ? atob(base64 + pad)
          : Buffer.from(base64 + pad, "base64").toString("binary");
        const bytes = Uint8Array.from(binString, c => c.charCodeAt(0));
        return JSON.parse(new TextDecoder().decode(bytes));
      } catch (error) {
        return null;
      }
    };

    const getPermissionsFromJwt = (token: string): string[] => {
      const payload = decodeJwtPayload(token);
      if (!payload) return [];
      const rbac = Array.isArray(payload.permissions) ? payload.permissions : [];
      const scope = typeof payload.scope === "string"
        ? payload.scope.split(" ").filter(Boolean)
        : [];
      return [...new Set([...rbac, ...scope])];
    };

    const hasPermission = (token: string | null | undefined, perm: string): boolean => {
      if (!token) return false;
      const permissions = getPermissionsFromJwt(token);
      return permissions.includes(perm);
    };

    const providerData = auth.providerData as { accessToken?: string } | null | undefined;
    const token = providerData?.accessToken;
    return hasPermission(token, portalPerm);
  };
};

/**
 * Verifica el permiso del portal + dev_portal:access:keycomps
 */
export const hasKeycompsPermissionDisplay = () => {
  return ({ auth }: { auth: any }) => {
    if (!auth?.isAuthenticated || !auth?.providerData?.accessToken) {
      return false;
    }

    const portalPerm = 'keyway:access:dev_portal';
    const requiredPerm = 'dev_portal:access:keycomps';

    const decodeJwtPayload = (token: string): any => {
      try {
        const parts = token.split(".");
        if (parts.length !== 3) return null;
        const payload = parts[1];
        const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
        const pad = "=".repeat((4 - (base64.length % 4 || 4)) % 4);
        const binString = typeof atob !== "undefined"
          ? atob(base64 + pad)
          : Buffer.from(base64 + pad, "base64").toString("binary");
        const bytes = Uint8Array.from(binString, c => c.charCodeAt(0));
        return JSON.parse(new TextDecoder().decode(bytes));
      } catch (error) {
        return null;
      }
    };

    const getPermissionsFromJwt = (token: string): string[] => {
      const payload = decodeJwtPayload(token);
      if (!payload) return [];
      const rbac = Array.isArray(payload.permissions) ? payload.permissions : [];
      const scope = typeof payload.scope === "string"
        ? payload.scope.split(" ").filter(Boolean)
        : [];
      return [...new Set([...rbac, ...scope])];
    };

    const hasPermission = (token: string | null | undefined, perm: string): boolean => {
      if (!token) return false;
      const permissions = getPermissionsFromJwt(token);
      return permissions.includes(perm);
    };

    const providerData = auth.providerData as { accessToken?: string } | null | undefined;
    const token = providerData?.accessToken;
    const hasPortal = hasPermission(token, portalPerm);
    const hasRequired = hasPermission(token, requiredPerm);
    return hasPortal && hasRequired;
  };
};

/**
 * Verifica el permiso del portal + dev_portal:access:keycomps_partner
 */
export const hasKeycompsPartnerPermissionDisplay = () => {
  return ({ auth }: { auth: any }) => {
    if (!auth?.isAuthenticated || !auth?.providerData?.accessToken) {
      return false;
    }

    const portalPerm = 'keyway:access:dev_portal';
    const requiredPerm = 'dev_portal:access:keycomps_partner';

    const decodeJwtPayload = (token: string): any => {
      try {
        const parts = token.split(".");
        if (parts.length !== 3) return null;
        const payload = parts[1];
        const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
        const pad = "=".repeat((4 - (base64.length % 4 || 4)) % 4);
        const binString = typeof atob !== "undefined"
          ? atob(base64 + pad)
          : Buffer.from(base64 + pad, "base64").toString("binary");
        const bytes = Uint8Array.from(binString, c => c.charCodeAt(0));
        return JSON.parse(new TextDecoder().decode(bytes));
      } catch (error) {
        return null;
      }
    };

    const getPermissionsFromJwt = (token: string): string[] => {
      const payload = decodeJwtPayload(token);
      if (!payload) return [];
      const rbac = Array.isArray(payload.permissions) ? payload.permissions : [];
      const scope = typeof payload.scope === "string"
        ? payload.scope.split(" ").filter(Boolean)
        : [];
      return [...new Set([...rbac, ...scope])];
    };

    const hasPermission = (token: string | null | undefined, perm: string): boolean => {
      if (!token) return false;
      const permissions = getPermissionsFromJwt(token);
      return permissions.includes(perm);
    };

    const providerData = auth.providerData as { accessToken?: string } | null | undefined;
    const token = providerData?.accessToken;
    const hasPortal = hasPermission(token, portalPerm);
    const hasRequired = hasPermission(token, requiredPerm);
    return hasPortal && hasRequired;
  };
};

/**
 * Verifica el permiso del portal + dev_portal:access:deal_room
 */
export const hasDealRoomPermissionDisplay = () => {
  return ({ auth }: { auth: any }) => {
    if (!auth?.isAuthenticated || !auth?.providerData?.accessToken) {
      return false;
    }

    const portalPerm = 'keyway:access:dev_portal';
    const requiredPerm = 'dev_portal:access:deal_room';

    const decodeJwtPayload = (token: string): any => {
      try {
        const parts = token.split(".");
        if (parts.length !== 3) return null;
        const payload = parts[1];
        const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
        const pad = "=".repeat((4 - (base64.length % 4 || 4)) % 4);
        const binString = typeof atob !== "undefined"
          ? atob(base64 + pad)
          : Buffer.from(base64 + pad, "base64").toString("binary");
        const bytes = Uint8Array.from(binString, c => c.charCodeAt(0));
        return JSON.parse(new TextDecoder().decode(bytes));
      } catch (error) {
        return null;
      }
    };

    const getPermissionsFromJwt = (token: string): string[] => {
      const payload = decodeJwtPayload(token);
      if (!payload) return [];
      const rbac = Array.isArray(payload.permissions) ? payload.permissions : [];
      const scope = typeof payload.scope === "string"
        ? payload.scope.split(" ").filter(Boolean)
        : [];
      return [...new Set([...rbac, ...scope])];
    };

    const hasPermission = (token: string | null | undefined, perm: string): boolean => {
      if (!token) return false;
      const permissions = getPermissionsFromJwt(token);
      return permissions.includes(perm);
    };

    const providerData = auth.providerData as { accessToken?: string } | null | undefined;
    const token = providerData?.accessToken;
    const hasPortal = hasPermission(token, portalPerm);
    const hasRequired = hasPermission(token, requiredPerm);
    return hasPortal && hasRequired;
  };
};

/**
 * Verifica el permiso del portal + dev_portal:access:keydocs
 */
export const hasKeydocsPermissionDisplay = () => {
  return ({ auth }: { auth: any }) => {
    if (!auth?.isAuthenticated || !auth?.providerData?.accessToken) {
      return false;
    }

    const portalPerm = 'keyway:access:dev_portal';
    const requiredPerm = 'dev_portal:access:keydocs';

    const decodeJwtPayload = (token: string): any => {
      try {
        const parts = token.split(".");
        if (parts.length !== 3) return null;
        const payload = parts[1];
        const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
        const pad = "=".repeat((4 - (base64.length % 4 || 4)) % 4);
        const binString = typeof atob !== "undefined"
          ? atob(base64 + pad)
          : Buffer.from(base64 + pad, "base64").toString("binary");
        const bytes = Uint8Array.from(binString, c => c.charCodeAt(0));
        return JSON.parse(new TextDecoder().decode(bytes));
      } catch (error) {
        return null;
      }
    };

    const getPermissionsFromJwt = (token: string): string[] => {
      const payload = decodeJwtPayload(token);
      if (!payload) return [];
      const rbac = Array.isArray(payload.permissions) ? payload.permissions : [];
      const scope = typeof payload.scope === "string"
        ? payload.scope.split(" ").filter(Boolean)
        : [];
      return [...new Set([...rbac, ...scope])];
    };

    const hasPermission = (token: string | null | undefined, perm: string): boolean => {
      if (!token) return false;
      const permissions = getPermissionsFromJwt(token);
      return permissions.includes(perm);
    };

    const providerData = auth.providerData as { accessToken?: string } | null | undefined;
    const token = providerData?.accessToken;
    const hasPortal = hasPermission(token, portalPerm);
    const hasRequired = hasPermission(token, requiredPerm);
    return hasPortal && hasRequired;
  };
};
